type KeyEventLike = Pick<
  KeyboardEvent,
  'key' | 'code' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'
>;

const MODIFIER_KEYS = new Set([
  'Meta',
  'Control',
  'Alt',
  'AltGraph',
  'Shift',
  'CapsLock',
]);

/**
 * Turns a keydown into a tinykeys binding such as `$mod+Shift+KeyP`.
 * Uses `code` rather than `key` because macOS Option rewrites `key`.
 * Returns null for a lone modifier or a key without Cmd/Ctrl/Alt, so a
 * bare letter can never become a global shortcut.
 */
export function keybindingFromEvent(
  e: KeyEventLike,
  isMac: boolean
): string | null {
  if (MODIFIER_KEYS.has(e.key)) return null;
  const mod = isMac ? e.metaKey : e.ctrlKey;
  const other = isMac ? e.ctrlKey : e.metaKey;
  if (!mod && !other && !e.altKey) return null;
  return [
    mod && '$mod',
    other && (isMac ? 'Control' : 'Meta'),
    e.altKey && 'Alt',
    e.shiftKey && 'Shift',
    e.code,
  ]
    .filter(Boolean)
    .join('+');
}

const MAC_NAMES: Record<string, string> = {
  $mod: '⌘',
  Meta: '⌘',
  Control: '⌃',
  Alt: '⌥',
  Shift: '⇧',
};
const OTHER_NAMES: Record<string, string> = {
  $mod: 'Ctrl',
  Control: 'Ctrl',
  Meta: 'Win',
  Alt: 'Alt',
  Shift: 'Shift',
};

/** `$mod+Shift+KeyP` → `['⌘', '⇧', 'P']` on macOS, `['Ctrl', 'Shift', 'P']` elsewhere. */
export function formatKeybinding(binding: string, isMac: boolean): string[] {
  const names = isMac ? MAC_NAMES : OTHER_NAMES;
  return binding
    .split('+')
    .filter(Boolean)
    .map(part => names[part] ?? part.replace(/^(Key|Digit)/, ''));
}
